import { useEventEmitter } from 'ahooks';
import { useEffect, useRef } from 'react';
import { tSocketNewQr } from '../../components/Sidebar/components/Modals/ConnectionModal/QrCode/QrCode.types.ts';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { WAPI_EVENTS } from 'src/hooks/analytics/wapiAnalytics.constants';
import { useWapiAnalytics } from 'src/hooks/analytics/useWapiAnalytics.hook';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from '../../lib/logging';
import { sentry } from '../../sentry/sentry.singleton.ts';
import { useACL } from '../ACLProvider/ACL.hook.ts';
import { useSubscriptionContext } from '../SubscriptionProvider/Subscription.context.ts';
import { useUserContext } from '../UserProvider/User.context';
import { useWorkspaceContext } from '../WorkspaceProvider/Workspace.context';
import { socketManager } from './Socket.singleton';
import {
  CATCH_UP_DEBOUNCE_MS,
  CATCH_UP_SOCKET_THRESHOLD_MS,
  CATCH_UP_VISIBILITY_THRESHOLD_MS,
  SENTRY_ERROR_ESCALATION_THRESHOLD,
  initialState,
} from './Sockets.constants';
import {
  tBackendUserInvitedEvent,
  tBackendWorkspaceUserActivatedEvent,
  TBillingMigrationEvent,
  tBillingQuotaUsageUpdatedEvent,
  tChatContactEvent,
  tChatEvent,
  tChatStatusUpdatedEvent,
  tCreateNewWapiTemplateEvent,
  tFunnelDeletedEvent,
  tFunnelDeletedProgressEvent,
  tFunnelEvent,
  tFunnelLeadCount,
  tLeadCreatedEvent,
  tLeadDeletedEvent,
  tLeadMessageEvent,
  tLeadUpdatedEvent,
  tNotification,
  tReconnectionEvent,
  tSession,
  tSlbEscalatedEvent,
  tSlbProcessingEvent,
  tSlbSessionStateChangedEvent,
  tSlbStageAdvancedEvent,
  tSlbFunnelMovedEvent,
  tSlbSessionExpiredEvent,
  tSlbSessionUpdatedEvent,
  tSlbSessionGateFailedEvent,
  tSlbEngagementChangedEvent,
  tTemplates,
  tAccountHealthUpdatedEvent,
  tCampaignDeletedEvent,
  tCampaignCancelledEvent,
  tWapiNotificationCreatedEvent,
  tWapiAccountConnectionUpdatedEvent,
  tProfilingStartedEvent,
  tProfilingCompletedEvent,
  tSyncContactsCreatedEvent,
  tSyncContactsUpdatedEvent,
  tSyncChunksCompleteEvent,
  tAutomationStateChangedEvent,
  tChallengeUpdatedEvent,
  tReactionSocket,
  tUpdateWapiTemplateEvent,
  tUseSockets,
  tWalletBalanceUpdatedEvent,
  tWorkspaceAccountUpdateEvent,
  tTikTokAccountUpdateEvent,
  tWorkspaceUpdatedEvent,
  tWorkspaceUsersUpdatedEvent,
  tWorkspaceUserUpdatedEvent,
} from './Sockets.types';
import { isEmpty } from 'lodash';
import { eLeadbotState } from 'src/pages/MessengerV2/Messenger.enum.ts';
import { ChatService } from 'src/components/Chat/Chat.service';
import { localizeAgentNote, SLB_ESCALATION_NOTE_TITLE } from 'src/constants/smartleadbot.constants';
import {
  transformActivatedEventToUserUpdate,
  transformInvitedEventToUserUpdate,
} from './Sockets.functions';

const useSockets = (): tUseSockets => {
  const { hasSession, currentUser, onSignOut } = useUserContext();
  const { canLeadsBeCreatedOrActivated } = useACL();
  const canLeadsBeCreatedOrActivatedRef = useRef(canLeadsBeCreatedOrActivated);
  canLeadsBeCreatedOrActivatedRef.current = canLeadsBeCreatedOrActivated;

  const {
    handleWorkspaceUpdate,
    handleWorkspaceUserUpdated,
    handleWorkspaceUserUpdate,
    currentWorkspace,
  } = useWorkspaceContext();
  const { handleFeatureUpdate, subscription } = useSubscriptionContext();
  const { trackEvent } = useAnalytics();
  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;
  const { trackWapiEvent } = useWapiAnalytics();
  const trackWapiEventRef = useRef(trackWapiEvent);
  trackWapiEventRef.current = trackWapiEvent;

  // Refs for workspace/user to avoid stale closures in reconnect()
  const currentWorkspaceIdRef = useRef(currentWorkspace?.id);
  currentWorkspaceIdRef.current = currentWorkspace?.id;
  const currentUserAuthIdRef = useRef(currentUser?.authId);
  currentUserAuthIdRef.current = currentUser?.authId;
  const currentUserUuidRef = useRef(currentUser?.uuid);
  currentUserUuidRef.current = currentUser?.uuid;
  const currentUserEmailRef = useRef(currentUser?.email);
  currentUserEmailRef.current = currentUser?.email;

  const chatInsertedEvent = useEventEmitter<tChatEvent>();
  const chatMessageBulkInsertFinishedEvent = useEventEmitter<void>();
  const chatUpdatedEvent = useEventEmitter<tChatEvent>();
  const chatContactInsertedEvent = useEventEmitter<tChatContactEvent>();
  const chatContactUpdatedEvent = useEventEmitter<tChatContactEvent>();
  const chatStatusUpdatedEvent = useEventEmitter<tChatStatusUpdatedEvent>();
  const newQREvent = useEventEmitter<tSocketNewQr>();
  const metaMessengerConnectedEvent = useEventEmitter<void>();
  const metaMessengerDisconnectedEvent = useEventEmitter<void>();
  const tiktokAccountUpdateEvent = useEventEmitter<tTikTokAccountUpdateEvent>();
  const workspaceAccountUpdateEvent =
    useEventEmitter<tWorkspaceAccountUpdateEvent>();
  const leadUpdatedEvent = useEventEmitter<tLeadUpdatedEvent>();
  const leadDeletedEvent = useEventEmitter<tLeadDeletedEvent>();
  const leadCreatedEvent = useEventEmitter<tLeadCreatedEvent>();
  const leadMessageEvent = useEventEmitter<tLeadMessageEvent>();
  const profileUpdatedEvent = useEventEmitter<void>();
  const profilingStartedEvent =
    useEventEmitter<tProfilingStartedEvent>();
  const profilingCompletedEvent =
    useEventEmitter<tProfilingCompletedEvent>();
  const syncContactsCreatedEvent =
    useEventEmitter<tSyncContactsCreatedEvent>();
  const syncContactsUpdatedEvent =
    useEventEmitter<tSyncContactsUpdatedEvent>();
  const syncChunksCompleteEvent =
    useEventEmitter<tSyncChunksCompleteEvent>();
  const funnelCreatedEvent = useEventEmitter<tFunnelEvent>();
  const funnelUpdatedEvent = useEventEmitter<tFunnelEvent>();
  const funnelDeletedEvent = useEventEmitter<tFunnelDeletedEvent>();
  const createNewWapiTemplateEvent =
    useEventEmitter<tCreateNewWapiTemplateEvent>();
  const updateWapiTemplateEvent = useEventEmitter<tUpdateWapiTemplateEvent>();

  const workspaceUsersUpdatedEvent =
    useEventEmitter<tWorkspaceUsersUpdatedEvent>();
  const workspaceUserUpdatedEvent =
    useEventEmitter<tWorkspaceUserUpdatedEvent>();
  const workspaceUpdatedEvent = useEventEmitter<tWorkspaceUpdatedEvent>();
  // Centro de Retos (AGI-498): progress pushed by workspace-service.
  const challengeUpdatedEvent = useEventEmitter<tChallengeUpdatedEvent>();
  const newNotificationEvent = useEventEmitter<tNotification>();
  const funnelDeletedProgressEvent =
    useEventEmitter<tFunnelDeletedProgressEvent>();
  const metaMessengerTemplateUpdate = useEventEmitter<tTemplates>();
  const userSessionLogOutEmitter = useEventEmitter<tSession>();
  const billingQuotaUsageUpdated =
    useEventEmitter<tBillingQuotaUsageUpdatedEvent>();
  const funnelLeadCountUpdated = useEventEmitter<tFunnelLeadCount>();
  const billingMigrationStartedEvent =
    useEventEmitter<TBillingMigrationEvent>();
  const billingMigrationFinishedEvent =
    useEventEmitter<TBillingMigrationEvent>();
  const walletBalanceUpdatedEvent =
    useEventEmitter<tWalletBalanceUpdatedEvent>();
  const catchUpEvent = useEventEmitter<tReconnectionEvent>();
  const slbProcessingEvent = useEventEmitter<tSlbProcessingEvent>();
  const slbEscalatedEvent = useEventEmitter<tSlbEscalatedEvent>();
  const slbSessionStateChangedEvent = useEventEmitter<tSlbSessionStateChangedEvent>();
  const slbStageAdvancedEvent = useEventEmitter<tSlbStageAdvancedEvent>();
  const slbFunnelMovedEvent = useEventEmitter<tSlbFunnelMovedEvent>();
  const slbSessionExpiredEvent = useEventEmitter<tSlbSessionExpiredEvent>();
  const slbSessionUpdatedEvent = useEventEmitter<tSlbSessionUpdatedEvent>();
  const slbSessionGateFailedEvent = useEventEmitter<tSlbSessionGateFailedEvent>();
  const slbEngagementChangedEvent = useEventEmitter<tSlbEngagementChangedEvent>();
  const automationStateChangedEvent = useEventEmitter<tAutomationStateChangedEvent>();
  const accountHealthUpdatedEvent =
    useEventEmitter<tAccountHealthUpdatedEvent>();
  const campaignDeletedEvent = useEventEmitter<tCampaignDeletedEvent>();
  const campaignCancelledEvent = useEventEmitter<tCampaignCancelledEvent>();
  const wapiNotificationCreatedEvent =
    useEventEmitter<tWapiNotificationCreatedEvent>();
  const wapiAccountConnectionUpdatedEvent =
    useEventEmitter<tWapiAccountConnectionUpdatedEvent>();
  const reactionInsertedEvent = useEventEmitter<tReactionSocket>();
  const reactionRemovedEvent = useEventEmitter<tReactionSocket>();

  // Tracks registered business event listeners for selective removal
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const businessListenersRef = useRef<Array<[string, (...args: any[]) => void]>>([]);
  // Tracks system-level socket listeners (connect, disconnect, error, connect_error)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const systemListenersRef = useRef<Array<[string, (...args: any[]) => void]>>([]);
  // Tracks manager-level listeners (reconnect_error, reconnect_failed)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const managerListenersRef = useRef<Array<[string, (...args: any[]) => void]>>([]);
  // Tracks when the socket disconnected for catch-up duration calculation
  const disconnectedAtRef = useRef<number | null>(null);
  // Tracks when the tab was hidden for catch-up duration calculation
  const hiddenAtRef = useRef<number | null>(null);
  // Debounces catch-up emissions to prevent duplicate refetches when both
  // visibilitychange and socket reconnect fire within milliseconds (e.g. wake-from-sleep)
  const lastCatchUpRef = useRef<number>(0);
  // Tracks consecutive connection errors for Sentry throttling
  const consecutiveErrorsRef = useRef(0);
  // Debounces reconnect() calls — coalesces same-tick invocations from
  // connect handler, subscription effect, and bootstrap check.
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!currentUser?.authId || !currentWorkspace?.id) return;

    socketManager.connect();

    // Helper to register and track system-level socket listeners
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onSystem = (event: string, handler: (...args: any[]) => void) => {
      socketManager.instance.on(event, handler);
      systemListenersRef.current.push([event, handler]);
    };

    // Helper to register and track manager-level listeners
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onManager = (event: string, handler: (...args: any[]) => void) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      socketManager.instance.io.on(event as any, handler);
      managerListenersRef.current.push([event, handler]);
    };

    const buildSocketSentryContext = () => ({
      user: {
        id: currentUser?.authId ?? '',
        workspaceId: currentWorkspace?.id ?? '',
      },
      extra: {
        socketId: socketManager.instance.id,
        connected: socketManager.instance.connected,
        transport: socketManager.instance.io?.engine?.transport?.name,
      },
    });

    // If already connected, register listeners immediately. Otherwise, the
    // persistent 'connect' handler below will call reconnect() once connected.
    if (socketManager.instance.connected) {
      scheduleReconnect();
    }

    // System listeners — registered once, preserved across business listener refreshes.
    // These survive reconnect() because reconnect() only removes business listeners.
    // This single handler covers both the initial connection and all subsequent
    // reconnections — no separate once('connect') needed.
    onSystem('connect', () => {
      LLog.info(
        {
          component: LOG_COMPONENTS.SOCKET,
          method: 'connect',
          operation: LOG_OPERATIONS.SOCKET_CONNECT,
        },
        'Socket connected',
      );
      consecutiveErrorsRef.current = 0;
      scheduleReconnect();
    });

    onSystem('disconnect', () => {
      LLog.info(
        {
          component: LOG_COMPONENTS.SOCKET,
          method: 'disconnect',
          operation: LOG_OPERATIONS.SOCKET_DISCONNECT,
        },
        'Socket disconnected',
      );
      disconnectedAtRef.current = Date.now();
      // Socket.IO's built-in exponential backoff handles reconnection automatically.
      // No manual setTimeout/connect needed here.
    });

    const handleOnline = () => {
      LLog.info(
        {
          component: LOG_COMPONENTS.SOCKET,
          method: 'online',
          operation: LOG_OPERATIONS.SOCKET_CONNECT,
        },
        'Network connection restored',
      );
      // Nudge-only: let Socket.IO reconnect if not already connected.
      // Avoids disconnect() which destroys the session ID needed for CSR.
      if (!socketManager.instance.connected) {
        socketManager.connect();
        // The system 'connect' listener will call reconnect() once connected
      }
    };

    const handleOffline = () => {
      LLog.info(
        {
          component: LOG_COMPONENTS.SOCKET,
          method: 'offline',
          operation: LOG_OPERATIONS.SOCKET_DISCONNECT,
        },
        'Network connection lost',
      );
      // Do NOT call socketManager.disconnect() — that tells Socket.IO the
      // disconnection was intentional, destroying the session ID needed for
      // Connection State Recovery. The socket will detect the network loss
      // naturally via transport failure.
    };

    // Debounced catch-up emitter — prevents duplicate refetches when both
    // visibilitychange and socket reconnect fire within milliseconds.
    const emitCatchUp = (event: tReconnectionEvent) => {
      const now = Date.now();
      if (now - lastCatchUpRef.current < CATCH_UP_DEBOUNCE_MS) return;
      lastCatchUpRef.current = now;
      catchUpEvent.emit(event);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      // visible
      const hiddenDurationMs = hiddenAtRef.current !== null
        ? Date.now() - hiddenAtRef.current
        : 0;
      hiddenAtRef.current = null;

      if (!socketManager.instance.connected) {
        LLog.info(
          {
            component: LOG_COMPONENTS.SOCKET,
            method: 'visibilitychange',
            operation: LOG_OPERATIONS.SOCKET_CONNECT,
          },
          'Tab resumed — nudging socket reconnect',
        );
        socketManager.connect();
        // reconnect() will emit catch-up via disconnectedAtRef
      } else if (hiddenDurationMs > CATCH_UP_VISIBILITY_THRESHOLD_MS && disconnectedAtRef.current !== null) {
        // Only emit visibility-based catch-up if the socket actually disconnected
        // while hidden. If it stayed connected, all events flowed normally.
        emitCatchUp({ offlineDurationMs: hiddenDurationMs, reason: 'tab_resume' });
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    onSystem('error', (error) => {
      LLog.error(
        {
          component: LOG_COMPONENTS.SOCKET,
          method: 'error',
          operation: LOG_OPERATIONS.SOCKET_ERROR,
        },
        'Socket error occurred',
        error,
      );
      const ctx = buildSocketSentryContext();
      sentry.captureException(error, {
        user: ctx.user,
        extra: { endpoint: 'socketManager.instance.error', ...ctx.extra },
      });
    });

    onSystem('connect_error', (error) => {
      consecutiveErrorsRef.current += 1;
      LLog.error(
        {
          component: LOG_COMPONENTS.SOCKET,
          method: 'connect_error',
          operation: LOG_OPERATIONS.SOCKET_ERROR,
        },
        'Socket connection error',
        error,
      );
      const ctx = buildSocketSentryContext();
      // Record individual errors as breadcrumbs to preserve context without
      // flooding Sentry. Only escalate to captureException after N consecutive
      // failures to avoid quota exhaustion during prolonged outages.
      sentry.addBreadcrumb({
        category: 'socket',
        message: `connect_error #${consecutiveErrorsRef.current}`,
        level: 'warning',
        data: { endpoint: 'socketManager.instance.connect_error', ...ctx.extra },
      });
      if (consecutiveErrorsRef.current === SENTRY_ERROR_ESCALATION_THRESHOLD) {
        sentry.captureException(error, {
          user: ctx.user,
          extra: {
            endpoint: 'socketManager.instance.connect_error',
            consecutiveErrors: consecutiveErrorsRef.current,
            ...ctx.extra,
          },
          fingerprint: ['socket-connect-error'],
        });
      }
    });

    // Manager-level events — these are emitted by the Socket.IO Manager, not
    // the Socket instance. Using socket.io.on() ensures they are captured
    // correctly in Socket.IO v4.
    onManager('reconnect_error', (error) => {
      LLog.error(
        {
          component: LOG_COMPONENTS.SOCKET,
          method: 'reconnect_error',
          operation: LOG_OPERATIONS.SOCKET_ERROR,
        },
        'Socket reconnection error',
        error,
      );
      const ctx = buildSocketSentryContext();
      // Breadcrumb only — reconnect_error fires on every failed attempt and
      // would flood Sentry with infinite reconnection attempts. The escalation
      // is handled by the connect_error counter above.
      sentry.addBreadcrumb({
        category: 'socket',
        message: 'reconnect_error',
        level: 'warning',
        data: { endpoint: 'socketManager.instance.io.reconnect_error', ...ctx.extra },
      });
    });

    onManager('reconnect_failed', () => {
      LLog.error(
        {
          component: LOG_COMPONENTS.SOCKET,
          method: 'reconnect_failed',
          operation: LOG_OPERATIONS.SOCKET_ERROR,
        },
        'Socket reconnection failed — all attempts exhausted',
      );
      const ctx = buildSocketSentryContext();
      sentry.captureException(new Error('Socket reconnection failed'), {
        user: ctx.user,
        extra: { endpoint: 'socketManager.instance.io.reconnect_failed', ...ctx.extra },
      });
    });

    return () => {
      // Cancel any pending debounced reconnect to prevent stale joins after unmount
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Remove system socket listeners (connect, disconnect, error, connect_error)
      for (const [event, handler] of systemListenersRef.current) {
        socketManager.instance.off(event, handler);
      }
      systemListenersRef.current = [];

      // Remove manager-level listeners (reconnect_error, reconnect_failed)
      for (const [event, handler] of managerListenersRef.current) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        socketManager.instance.io.off(event as any, handler);
      }
      managerListenersRef.current = [];

      removeBusinessListeners();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      socketManager.disconnect();
    };
  }, [currentUser?.authId, currentWorkspace?.id]);

  // Re-configure socket listeners when subscription updates.
  // This is not a network reconnect, so skip catch-up emission.
  useEffect(() => {
    if (socketManager.instance.connected && subscription?.planCode) {
      scheduleReconnect({ emitCatchUp: false });
    }
  }, [subscription?.planCode]);

  function removeBusinessListeners() {
    for (const [event, handler] of businessListenersRef.current) {
      socketManager.instance.off(event, handler);
    }
    businessListenersRef.current = [];
  }

  function scheduleReconnect(opts?: { emitCatchUp?: boolean }) {
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      reconnect(opts);
    }, 0);
  }

  function reconnect({ emitCatchUp: shouldEmitCatchUp = true } = {}) {
    // Remove only business listeners — system listeners (connect, disconnect,
    // error, connect_error, reconnect_error, reconnect_failed) are preserved.
    removeBusinessListeners();

    const socket = socketManager.instance;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const listeners: Array<[string, (...args: any[]) => void]> = [];

    // Helper that registers a listener AND tracks it for later cleanup
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const on = (event: string, handler: (...args: any[]) => void) => {
      socket.on(event, handler);
      listeners.push([event, handler]);
    };

    socket.emit('join.room', currentWorkspaceIdRef.current);
    socket.emit('join.room', `${currentWorkspaceIdRef.current}:${currentUserAuthIdRef.current}`);
    socket.emit('join.room', currentUserUuidRef.current);
    on(`chat_event.inserted`, async (newEvent: tChatEvent) => {
      chatInsertedEvent.emit(newEvent);
    });
    on(`chat_event.updated`, (update: tChatEvent) => {
      chatUpdatedEvent.emit(update);
    });
    on(`chat.contact.inserted`, async (newEvent: tChatContactEvent) => {
      chatContactInsertedEvent.emit(newEvent);
    });
    on(`chat.contact.updated`, (update: tChatContactEvent) => {
      chatContactUpdatedEvent.emit(update);
    });
    on('chat.message.bulk_insert.finished', () => {
      chatMessageBulkInsertFinishedEvent.emit();
    });
    on(
      'chat.status.updated',
      (chatStatusEvent: tChatStatusUpdatedEvent) => {
        chatStatusUpdatedEvent.emit(chatStatusEvent);
      },
    );
    on(`workspace_event.new_qr`, (newQR: tSocketNewQr) => {
      newQREvent.emit(newQR);
    });
    on('meta.messenger.connected', (data) => {
      metaMessengerConnectedEvent.emit(data);
    });
    on('meta.messenger.disconnected', () => {
      metaMessengerDisconnectedEvent.emit();
    });
    for (const event of ['tiktok.account.connected', 'tiktok.account.revoked']) {
      on(event, (data: tTikTokAccountUpdateEvent) => {
        if (data?.workspace_id === currentWorkspaceIdRef.current) {
          tiktokAccountUpdateEvent.emit(data);
        }
      });
    }
    on(
      `workspace_event.account_update`,
      (status: tWorkspaceAccountUpdateEvent) => {
        workspaceAccountUpdateEvent.emit(status);
        if (status.connected) {
          trackEventRef.current('front.dashboard.integrations.whatsapp_scan.success', {
            displayName: 'Conexión exitosa whatsapp',
            description:
              'Cuántas usuarias se conectaron exitosamente a whatsapp',
            accountType: 'whatsapp',
            accountStatus: 'connected',
            ...(currentUserEmailRef.current ? { email: currentUserEmailRef.current } : {}),
          });
        }
      },
    );
    on('lead_event.updated', (leadEvent: tLeadUpdatedEvent) => {
      const isLeadActivatedEvent =
        !!leadEvent.action_type?.includes('changed_active');

      if (isLeadActivatedEvent && !canLeadsBeCreatedOrActivatedRef.current()) return;

      leadUpdatedEvent.emit(leadEvent);
    });
    on('lead_event.deleted', (leadDeleteEvent: tLeadDeletedEvent) => {
      leadDeletedEvent.emit(leadDeleteEvent);
    });
    on('lead_event.created', (leadCreateEvent: tLeadCreatedEvent) => {
      if (!canLeadsBeCreatedOrActivatedRef.current()) return;

      // Don't show leads when quota is exceeded
      if (leadCreateEvent.response.is_quota_exceeded) return;

      leadCreatedEvent.emit(leadCreateEvent);
    });
    on(
      'lead.message.created',
      (leadMessageCreated: tLeadMessageEvent) => {
        if (leadMessageCreated.from_me) return;

        const isMessageEmpty = isEmpty(leadMessageCreated.message);
        const isLeadbotSession =
          leadMessageCreated.leadbot_state === eLeadbotState.SESSION_FINISHED ||
          leadMessageCreated.leadbot_state === eLeadbotState.SESSION_ACTIVE;
        const isMessageFromLead =
          leadMessageCreated.from_me === false &&
          leadMessageCreated.lead_name &&
          !leadMessageCreated.created_by;

        if (isMessageEmpty && (isLeadbotSession || isMessageFromLead)) {
          return;
        }

        leadMessageEvent.emit(leadMessageCreated);
      },
    );
    on('profile_event.updated', (leadUpdateEvent) => {
      profileUpdatedEvent.emit(leadUpdateEvent);
    });
    on('profiling_event.started', (event: tProfilingStartedEvent) => {
      profilingStartedEvent.emit(event);
    });
    on('profiling_event.completed', (event: tProfilingCompletedEvent) => {
      profilingCompletedEvent.emit(event);
    });
    on('whatsapp_business.sync.contacts.created', (event: tSyncContactsCreatedEvent) => {
      syncContactsCreatedEvent.emit(event);
    });
    on('whatsapp_business.sync.contacts.updated', (event: tSyncContactsUpdatedEvent) => {
      syncContactsUpdatedEvent.emit(event);
    });
    on('whatsapp_business.sync.chunks.complete', (event: tSyncChunksCompleteEvent) => {
      syncChunksCompleteEvent.emit(event);
    });
    on('funnel_event.created', (funnelCreated: tFunnelEvent) => {
      funnelCreatedEvent.emit(funnelCreated);
    });
    on('funnel_event.updated', (funnelUpdated: tFunnelEvent) => {
      funnelUpdatedEvent.emit(funnelUpdated);
    });
    on('funnel_event.deleted', (funnelDeleted: tFunnelDeletedEvent) => {
      funnelDeletedEvent.emit(funnelDeleted);
    });
    on(
      'workspace_event.users_updated',
      (usersUpdatedEvent: tWorkspaceUsersUpdatedEvent) => {
        workspaceUsersUpdatedEvent.emit(usersUpdatedEvent);
      },
    );
    // Also listen to the singular form (without 'd') in case backend sends it
    on(
      'workspace_event.users_update',
      (usersUpdatedEvent: tWorkspaceUsersUpdatedEvent) => {
        workspaceUsersUpdatedEvent.emit(usersUpdatedEvent);
      },
    );
    on(
      'workspace_event.user_updated',
      (userUpdatedEvent: tWorkspaceUserUpdatedEvent) => {
        workspaceUserUpdatedEvent.emit(userUpdatedEvent);
      },
    );
    // Also listen to the singular form (without 'd') in case backend sends it
    on(
      'workspace_event.user_update',
      (userUpdatedEvent: tWorkspaceUserUpdatedEvent) => {
        workspaceUserUpdatedEvent.emit(userUpdatedEvent);
      },
    );
    // Listen for user activation event
    on(
      'workspace_event.user_activated',
      (userActivatedEvent: tBackendWorkspaceUserActivatedEvent) => {
        if (!userActivatedEvent.user) {
          LLog.warn(
            {
              component: LOG_COMPONENTS.SOCKET,
              method: 'workspace_event.user_activated',
              operation: LOG_OPERATIONS.DEBUG,
            },
            'Received user_activated event but user data is missing',
            { event: userActivatedEvent },
          );
          return;
        }

        const transformedUser =
          transformActivatedEventToUserUpdate(userActivatedEvent);

        workspaceUserUpdatedEvent.emit({
          eventType: 'activated',
          workspace_id: userActivatedEvent.workspace_id,
          user: transformedUser,
        });
      },
    );
    // Listen for user invitation event
    on(
      'workspace_event.user_invited',
      (userInvitedEvent: tBackendUserInvitedEvent) => {
        if (!userInvitedEvent.pendingUser?.user) {
          LLog.warn(
            {
              component: LOG_COMPONENTS.SOCKET,
              method: 'workspace_event.user_invited',
              operation: LOG_OPERATIONS.DEBUG,
            },
            'Received user_invited event but pendingUser data is missing',
            { event: userInvitedEvent },
          );
          return;
        }

        const transformedUser =
          transformInvitedEventToUserUpdate(userInvitedEvent);

        workspaceUserUpdatedEvent.emit({
          eventType: 'invited',
          workspace_id:
            userInvitedEvent.workspace_id ||
            userInvitedEvent.userInvitation.workspaceId,
          user: transformedUser,
        });
      },
    );
    on(
      'workspace_event.updated',
      (workspaceUpdated: tWorkspaceUpdatedEvent) => {
        workspaceUpdatedEvent.emit(workspaceUpdated);
      },
    );
    // Centro de Retos (AGI-498): progress pushed by workspace-service.
    on(
      'workspace_event.challenge_updated',
      (challengeUpdated: tChallengeUpdatedEvent) => {
        challengeUpdatedEvent.emit(challengeUpdated);
      },
    );
    on(
      'notification.push_notification.saved',
      (newNotification: tNotification) => {
        newNotificationEvent.emit(newNotification);
      },
    );
    on(`contact.updated.rules`, (template: tTemplates) => {
      metaMessengerTemplateUpdate.emit(template);
    });

    on('user.session.disconnected', (session: tSession) => {
      userSessionLogOutEmitter.emit(session);
    });
    on(
      `funnel_event.deleted_progress`,
      (progressData: tFunnelDeletedProgressEvent) => {
        funnelDeletedProgressEvent.emit(progressData);
      },
    );
    on(
      `feature_usage.updated`,
      (billingQuotaUsage: tBillingQuotaUsageUpdatedEvent) => {
        billingQuotaUsageUpdated.emit(billingQuotaUsage);
      },
    );
    on(`funnel_lead_count.updated`, (leadCount: tFunnelLeadCount) => {
      funnelLeadCountUpdated.emit(leadCount);
    });
    on(
      'whatsapp_business.template.created',
      (data: tCreateNewWapiTemplateEvent) => {
        createNewWapiTemplateEvent.emit(data);
      },
    );
    on(
      'whatsapp_business.template.status_update',
      (data: tUpdateWapiTemplateEvent) => {
        updateWapiTemplateEvent.emit(data);
      },
    );
    on('whatsapp_business.disconnected', () => {
      trackWapiEventRef.current(WAPI_EVENTS.INTEGRATION_DISCONNECTION, {});
    });
    on(
      'whatsapp_business.account.health_updated',
      (data: tAccountHealthUpdatedEvent) => {
        accountHealthUpdatedEvent.emit(data);
      },
    );
    on(
      'whatsapp_business.campaign.deleted',
      (data: tCampaignDeletedEvent) => {
        campaignDeletedEvent.emit(data);
      },
    );
    on(
      'whatsapp_business.campaign.cancelled',
      (data: tCampaignCancelledEvent) => {
        campaignCancelledEvent.emit(data);
      },
    );
    on(
      'whatsapp_business.notification.created',
      (data: tWapiNotificationCreatedEvent) => {
        wapiNotificationCreatedEvent.emit(data);
      },
    );
    // INT-1653 / INT-1676 — Meta took the number out of the API, gave it back,
    // or a reconnection outcome was written; IntegrationsProvider refetches.
    on(
      'whatsapp_business.account.connection_updated',
      (data: tWapiAccountConnectionUpdatedEvent) => {
        wapiAccountConnectionUpdatedEvent.emit(data);
      },
    );
    on('billing.migration.started', (data: TBillingMigrationEvent) => {
      billingMigrationStartedEvent.emit(data);
    });
    on('billing.migration.finished', (data: TBillingMigrationEvent) => {
      billingMigrationFinishedEvent.emit(data);
    });
    // Señal de invalidación de saldo (ADR-013): "el saldo cambió", sin montos.
    // El consumidor (WalletProvider) reconsulta; nunca se pinta lo empujado.
    on('wallet.balance.updated', (data: tWalletBalanceUpdatedEvent) => {
      walletBalanceUpdatedEvent.emit(data);
    });
    on('smartleadbot.message.processing', (data: tSlbProcessingEvent) => {
      slbProcessingEvent.emit(data);
    });
    on('smartleadbot.session.escalated', (data: tSlbEscalatedEvent) => {
      slbEscalatedEvent.emit(data);
      // Persist the handoff note globally so it's saved even if the chat isn't open.
      // The note surfaces in the chat's Notes section plus (in-chat) as the MessageBar
      // violet banner and (kanban) the PENDIENTE chip. There is no separate top-of-chat
      // banner — SLBEscalationBanner was removed in RET-870 as redundant UI.
      if (data.agent_note && data.chat_id) {
        // Cross-tab dedup: prevent duplicate notes when multiple tabs receive the
        // same escalation event. Each tab checks localStorage before persisting;
        // only the first tab within the TTL window creates the note.
        const dedupKey = `slb_escalation_note:${data.chat_id}`;
        const DEDUP_TTL_MS = 10_000;
        const previous = localStorage.getItem(dedupKey);
        if (previous && Date.now() - Number(previous) < DEDUP_TTL_MS) return;
        localStorage.setItem(dedupKey, String(Date.now()));

        const localizedNote = localizeAgentNote(data.agent_note);
        // Prefer the server-generated id from the escalated event so every operator's
        // tab posts under the same id; chats-service NoteCreator (RET-972) collapses the
        // concurrent inserts via PK upsert. crypto.randomUUID() remains as a defensive
        // fallback in case the event arrives before chatbot-service RET-971 deploys.
        // Use `||` rather than `??` so an empty-string `note_id` (Go's zero value
        // when the producer omits the field without a pointer/omitempty) also
        // falls back to a client UUID instead of POSTing `id: ""`.
        const noteId = data.note_id || crypto.randomUUID();
        ChatService.createNote({
          chatId: data.chat_id,
          data: {
            id: noteId,
            content: { text: localizedNote, attachments: [] },
            created_by: 'smart_leadbot',
            title: SLB_ESCALATION_NOTE_TITLE,
            note: { title: SLB_ESCALATION_NOTE_TITLE, type: 0 },
          },
        })
          .then(() => {
            // Cache the note id so a later state.changed{enabled:true} or "Tomar control"
            // click can delete this exact row without first fetching the chat's note list.
            // RET-870 — banner was re-appearing forever because the note was never cleared.
            // Key is namespaced by workspace_id so a chat_id collision across workspaces
            // (e.g. operator switches workspace) can't cause a cross-workspace deleteEvent.
            try {
              if (data.workspace_id) {
                localStorage.setItem(`slb_escalation_note_id:${data.workspace_id}:${data.chat_id}`, noteId);
              }
            } catch {
              // Quota/access failure. Cleanup will fall back to fetching the note list
              // on re-enable instead of deleting by stored id — non-fatal.
            }
          })
          .catch(() => {
            LLog.warn(
              { component: LOG_COMPONENTS.SOCKET, method: 'slbEscalatedEvent', operation: LOG_OPERATIONS.ERROR },
              'Failed to persist SLB escalation note',
            );
          });
      }
    });
    on('smartleadbot.session.state.changed', (data: tSlbSessionStateChangedEvent) => {
      // Notes are append-only. Re-enabling the bot does NOT delete the escalation note —
      // it remains as a historical record. The MessageBar banner and PENDIENTE kanban chip
      // clear via session-state events (MessageBar.content.tsx and Pipe.hook.tsx),
      // independent of note existence.
      slbSessionStateChangedEvent.emit(data);
    });
    on('smartleadbot.session.stage.advanced', (data: tSlbStageAdvancedEvent) => {
      slbStageAdvancedEvent.emit(data);
    });
    on('smartleadbot.session.funnel.moved', (data: tSlbFunnelMovedEvent) => {
      // RET-1311 — the Lead Agent moved this lead to a different funnel. The
      // durable note ("SmartLeadbot: cambio de embudo") is persisted by messaging
      // and arrives via the normal note-insert flow, so we do NOT create a note
      // here (unlike escalation). This event only surfaces the structured
      // funnel/stage identifiers that the in-chat deep-link consumes.
      slbFunnelMovedEvent.emit(data);
    });
    on('smartleadbot.session.expired', (data: tSlbSessionExpiredEvent) => {
      slbSessionExpiredEvent.emit(data);
    });
    on('smartleadbot.session.updated', (data: tSlbSessionUpdatedEvent) => {
      slbSessionUpdatedEvent.emit(data);
    });
    on('smartleadbot.session.gate_failed', (data: tSlbSessionGateFailedEvent) => {
      slbSessionGateFailedEvent.emit(data);
    });
    on('chatbot.session.engagement.changed', (data: tSlbEngagementChangedEvent) => {
      slbEngagementChangedEvent.emit(data);
    });
    on('automation.state.changed', (data: tAutomationStateChangedEvent) => {
      automationStateChangedEvent.emit(data);
    });
    on('reaction.inserted', (data: tReactionSocket) => {
      reactionInsertedEvent.emit(data);
    });
    on('reaction.removed', (data: tReactionSocket) => {
      reactionRemovedEvent.emit(data);
    });

    if (shouldEmitCatchUp && disconnectedAtRef.current !== null) {
      const offlineDurationMs = Date.now() - disconnectedAtRef.current;
      disconnectedAtRef.current = null;
      if (offlineDurationMs > CATCH_UP_SOCKET_THRESHOLD_MS) {
        // Debounce: skip if a catch-up was already emitted recently
        // (e.g. visibilitychange and socket reconnect firing back-to-back)
        const now = Date.now();
        if (now - lastCatchUpRef.current >= CATCH_UP_DEBOUNCE_MS) {
          lastCatchUpRef.current = now;
          catchUpEvent.emit({ offlineDurationMs, reason: 'socket_reconnect' });
        }
      }
    }

    businessListenersRef.current = listeners;
  }

  userSessionLogOutEmitter.useSubscription(async (session: tSession) => {
    if (
      session.user_id === currentUser?.uuid &&
      session.device_type === 'Desktop'
    ) {
      LLog.info(
        {
          component: LOG_COMPONENTS.AUTH,
          method: 'session',
          operation: LOG_OPERATIONS.AUTH,
        },
        'Session disconnected – signing out',
      );
      await onSignOut();
    }
  });
  workspaceUpdatedEvent.useSubscription(handleWorkspaceUpdate);
  workspaceUsersUpdatedEvent.useSubscription(handleWorkspaceUserUpdated);
  workspaceUserUpdatedEvent.useSubscription(handleWorkspaceUserUpdate);
  billingQuotaUsageUpdated.useSubscription(handleFeatureUpdate);

  if (!hasSession) {
    return initialState;
  }

  return {
    chatInsertedEvent,
    chatMessageBulkInsertFinishedEvent,
    chatUpdatedEvent,
    newQREvent,
    metaMessengerConnectedEvent,
    metaMessengerDisconnectedEvent,
    tiktokAccountUpdateEvent,
    workspaceAccountUpdateEvent,
    leadUpdatedEvent,
    leadDeletedEvent,
    leadCreatedEvent,
    leadMessageEvent,
    profileUpdatedEvent,
    funnelCreatedEvent,
    funnelUpdatedEvent,
    funnelDeletedEvent,
    workspaceUsersUpdatedEvent,
    workspaceUserUpdatedEvent,
    workspaceUpdatedEvent,
    challengeUpdatedEvent,
    newNotificationEvent,
    metaMessengerTemplateUpdate,
    userSessionLogOutEmitter,
    funnelDeletedProgressEvent,
    billingQuotaUsageUpdated,
    funnelLeadCountUpdated,
    chatContactInsertedEvent,
    chatContactUpdatedEvent,
    createNewWapiTemplateEvent,
    updateWapiTemplateEvent,
    chatStatusUpdatedEvent,
    billingMigrationStartedEvent,
    billingMigrationFinishedEvent,
    walletBalanceUpdatedEvent,
    catchUpEvent,
    slbProcessingEvent,
    slbEscalatedEvent,
    slbSessionStateChangedEvent,
    slbStageAdvancedEvent,
    slbFunnelMovedEvent,
    slbSessionExpiredEvent,
    slbSessionUpdatedEvent,
    slbSessionGateFailedEvent,
    slbEngagementChangedEvent,
    accountHealthUpdatedEvent,
    campaignDeletedEvent,
    campaignCancelledEvent,
    wapiNotificationCreatedEvent,
    wapiAccountConnectionUpdatedEvent,
    reactionInsertedEvent,
    reactionRemovedEvent,
    profilingStartedEvent,
    profilingCompletedEvent,
    syncContactsCreatedEvent,
    syncContactsUpdatedEvent,
    syncChunksCompleteEvent,
    automationStateChangedEvent,
  };
};

export default useSockets;
